/**
 * WalletContext - Manages wallet connection state for DEX mode
 * 
 * Supports:
 * - MetaMask
 * - WalletConnect
 * - Coinbase Wallet
 * - Other EIP-1193 compatible wallets
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Trading mode types
export type TradingMode = 'WALLET' | 'CUSTODIAL';

// Supported chains
export const SUPPORTED_CHAINS = {
  1: { name: 'Ethereum', symbol: 'ETH', rpcUrl: 'https://eth.llamarpc.com' },
  42161: { name: 'Arbitrum', symbol: 'ETH', rpcUrl: 'https://arb1.arbitrum.io/rpc' },
  137: { name: 'Polygon', symbol: 'MATIC', rpcUrl: 'https://polygon-rpc.com' },
  56: { name: 'BNB Chain', symbol: 'BNB', rpcUrl: 'https://bsc-dataseed.binance.org' },
  8453: { name: 'Base', symbol: 'ETH', rpcUrl: 'https://mainnet.base.org' },
  10: { name: 'Optimism', symbol: 'ETH', rpcUrl: 'https://mainnet.optimism.io' },
  43114: { name: 'Avalanche', symbol: 'AVAX', rpcUrl: 'https://api.avax.network/ext/bc/C/rpc' },
} as const;

// Wallet connection state
interface WalletState {
  isConnected: boolean;
  address: string | null;
  chainId: number | null;
  balance: string | null;
  isConnecting: boolean;
  error: string | null;
}

// Context value type
interface WalletContextValue {
  // State
  wallet: WalletState;
  tradingMode: TradingMode;
  
  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  switchChain: (chainId: number) => Promise<void>;
  setTradingMode: (mode: TradingMode) => void;
  
  // Helpers
  isChainSupported: (chainId: number) => boolean;
  getChainName: (chainId: number) => string;
  formatAddress: (address: string) => string;
}

// Default state
const defaultWalletState: WalletState = {
  isConnected: false,
  address: null,
  chainId: null,
  balance: null,
  isConnecting: false,
  error: null,
};

// Create context
const WalletContext = createContext<WalletContextValue | undefined>(undefined);

// Provider props
interface WalletProviderProps {
  children: ReactNode;
}

// Declare ethereum type for window
declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      on: (event: string, callback: (...args: unknown[]) => void) => void;
      removeListener: (event: string, callback: (...args: unknown[]) => void) => void;
      selectedAddress?: string;
    };
  }
}

export function WalletProvider({ children }: WalletProviderProps) {
  const [wallet, setWallet] = useState<WalletState>(defaultWalletState);
  const [tradingMode, setTradingMode] = useState<TradingMode>(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('tradingMode');
    return (saved as TradingMode) || 'CUSTODIAL';
  });

  // Save trading mode to localStorage
  useEffect(() => {
    localStorage.setItem('tradingMode', tradingMode);
  }, [tradingMode]);

  // Check if chain is supported
  const isChainSupported = useCallback((chainId: number) => {
    return chainId in SUPPORTED_CHAINS;
  }, []);

  // Get chain name
  const getChainName = useCallback((chainId: number) => {
    return SUPPORTED_CHAINS[chainId as keyof typeof SUPPORTED_CHAINS]?.name || `Chain ${chainId}`;
  }, []);

  // Format address (0x1234...5678)
  const formatAddress = useCallback((address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, []);

  // Get balance
  const fetchBalance = useCallback(async (address: string) => {
    if (!window.ethereum) return null;
    try {
      const balance = await window.ethereum.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      // Convert from hex wei to ETH
      const balanceInEth = parseInt(balance as string, 16) / 1e18;
      return balanceInEth.toFixed(4);
    } catch {
      return null;
    }
  }, []);

  // Handle account change
  const handleAccountsChanged = useCallback((...args: unknown[]) => {
    const accounts = args[0] as string[];
    if (!accounts || accounts.length === 0) {
      // User disconnected
      setWallet(defaultWalletState);
    } else {
      const address = accounts[0];
      fetchBalance(address).then(balance => {
        setWallet(prev => ({ ...prev, address, balance }));
      });
    }
  }, [fetchBalance]);

  // Handle chain change
  const handleChainChanged = useCallback((chainId: unknown) => {
    const chainIdNum = parseInt(chainId as string, 16);
    setWallet(prev => ({
      ...prev,
      chainId: chainIdNum,
      error: isChainSupported(chainIdNum) ? null : `Unsupported chain: ${getChainName(chainIdNum)}`,
    }));
  }, [isChainSupported, getChainName]);

  // Connect wallet
  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setWallet(prev => ({
        ...prev,
        error: 'No wallet detected. Please install MetaMask or another Web3 wallet.',
      }));
      return;
    }

    setWallet(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      }) as string[];

      // Get chain ID
      const chainIdHex = await window.ethereum.request({
        method: 'eth_chainId',
      }) as string;
      const chainId = parseInt(chainIdHex, 16);

      // Get balance
      const balance = await fetchBalance(accounts[0]);

      setWallet({
        isConnected: true,
        address: accounts[0],
        chainId,
        balance,
        isConnecting: false,
        error: isChainSupported(chainId) ? null : `Connected to unsupported chain: ${getChainName(chainId)}`,
      });

      // Auto-switch to wallet mode on successful connection
      setTradingMode('WALLET');

    } catch (error: unknown) {
      const err = error as { code?: number; message?: string };
      setWallet(prev => ({
        ...prev,
        isConnecting: false,
        error: err.code === 4001 
          ? 'Connection rejected by user' 
          : err.message || 'Failed to connect wallet',
      }));
    }
  }, [fetchBalance, isChainSupported, getChainName]);

  // Disconnect wallet
  const disconnect = useCallback(() => {
    setWallet(defaultWalletState);
    // Don't auto-switch mode, let user decide
  }, []);

  // Switch chain
  const switchChain = useCallback(async (chainId: number) => {
    if (!window.ethereum) {
      setWallet(prev => ({ ...prev, error: 'No wallet detected' }));
      return;
    }

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
    } catch (error: unknown) {
      const err = error as { code?: number };
      // Chain not added to wallet, try to add it
      if (err.code === 4902) {
        const chainConfig = SUPPORTED_CHAINS[chainId as keyof typeof SUPPORTED_CHAINS];
        if (chainConfig) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: `0x${chainId.toString(16)}`,
                chainName: chainConfig.name,
                nativeCurrency: {
                  name: chainConfig.symbol,
                  symbol: chainConfig.symbol,
                  decimals: 18,
                },
                rpcUrls: [chainConfig.rpcUrl],
              }],
            });
          } catch {
            setWallet(prev => ({ ...prev, error: `Failed to add ${chainConfig.name}` }));
          }
        }
      } else {
        setWallet(prev => ({ ...prev, error: 'Failed to switch chain' }));
      }
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (!window.ethereum) return;

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    // Check if already connected
    if (window.ethereum.selectedAddress) {
      connect();
    }

    return () => {
      if (!window.ethereum) return;
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [handleAccountsChanged, handleChainChanged, connect]);

  const value: WalletContextValue = {
    wallet,
    tradingMode,
    connect,
    disconnect,
    switchChain,
    setTradingMode,
    isChainSupported,
    getChainName,
    formatAddress,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

// Custom hook to use wallet context
export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
